// Phases finales — génération du tableau, résolution dynamique et classement final.
// Les matchs stockent des "sources" (1er du groupe A, vainqueur du Quart 1…) ;
// les équipes sont résolues en direct depuis les classements et scores, donc
// toute modification de score se propage automatiquement.
import { leagueSorter } from "@/lib/standings";

export const ROUND_LABELS = {
  r16: "Huitièmes de finale",
  qf: "Quarts de finale",
  sf: "Demi-finales",
  final: "Finale",
  third: "Petite finale",
  c_sf: "Classement 5e-8e",
  c5: "Match pour la 5e place",
  c7: "Match pour la 7e place",
};

export const ROUND_ORDER = ["r16", "qf", "sf", "final", "third", "c_sf", "c5", "c7"];

export const KO_FORMATS = [
  { value: "none", label: "Groupes uniquement" },
  { value: "sf", label: "Demi-finales + Finale" },
  { value: "qf", label: "Quarts + Demi-finales + Finale" },
  { value: "r16", label: "Huitièmes + Quarts + Demi-finales + Finale" },
];

const hasScore = (v) => v !== null && v !== undefined && v !== "";
export const isKOPlayed = (m) => {
  if (!hasScore(m.home_score) || !hasScore(m.away_score)) return false;
  if (Number(m.home_score) !== Number(m.away_score)) return true;
  return (
    hasScore(m.home_pens) &&
    hasScore(m.away_pens) &&
    Number(m.home_pens) !== Number(m.away_pens)
  );
};

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const mkMatch = (round, slot, label, home_source, away_source) => ({
  id: uid(),
  round,
  slot,
  label,
  home_source,
  away_source,
  home_score: null,
  away_score: null,
  date: "",
  venue: "",
});

const gsrc = (group, rank) => ({ type: "group", group, rank });
const seedSrc = (rank) => ({ type: "seed", rank });
const W = (m) => ({ type: "winner", match_id: m.id });
const L = (m) => ({ type: "loser", match_id: m.id });

export const groupNamesOf = (teams) => [
  ...new Set(teams.map((t) => (t.group || "").trim()).filter(Boolean)),
];

// Liste des qualifiés (sources seed) : top N de chaque groupe, ordonnés rang puis force
const seedSources = (count) =>
  Array.from({ length: count }, (_, i) => seedSrc(i + 1));

// Ordre de placement standard (1 et 2 se rencontrent le plus tard possible)
const seedOrder = (n) => {
  let r = [1, 2];
  while (r.length < n) {
    const next = [];
    const m = r.length * 2 + 1;
    r.forEach((s) => {
      next.push(s);
      next.push(m - s);
    });
    r = next;
  }
  return r;
};

const pairFirstRound = (round, labelPrefix, sources) => {
  const order = seedOrder(sources.length);
  const out = [];
  for (let i = 0; i < sources.length / 2; i += 1) {
    out.push(
      mkMatch(
        round,
        i + 1,
        `${labelPrefix} ${i + 1}`,
        sources[order[2 * i] - 1],
        sources[order[2 * i + 1] - 1],
      ),
    );
  }
  return out;
};

export function generateBracket({ teams, format, qualifiedPerGroup, thirdPlace, classification }) {
  if (!format || format === "none") return [];
  const groups = groupNamesOf(teams);
  const need = { sf: 4, qf: 8, r16: 16 }[format];
  const qualifiers = groups.length
    ? Math.min(qualifiedPerGroup, 99) * groups.length
    : teams.length;
  if (qualifiers < need || teams.length < need) return null;

  let first = [];
  if (format === "sf") {
    if (groups.length === 2 && qualifiedPerGroup === 2) {
      const [A, B] = groups;
      first = [
        mkMatch("sf", 1, "Demi-finale 1", gsrc(A, 1), gsrc(B, 2)),
        mkMatch("sf", 2, "Demi-finale 2", gsrc(B, 1), gsrc(A, 2)),
      ];
    } else {
      first = pairFirstRound("sf", "Demi-finale", seedSources(4));
    }
  } else if (format === "qf") {
    if (groups.length === 2 && qualifiedPerGroup === 4) {
      const [A, B] = groups;
      first = [
        mkMatch("qf", 1, "Quart 1", gsrc(A, 1), gsrc(B, 4)),
        mkMatch("qf", 2, "Quart 2", gsrc(A, 2), gsrc(B, 3)),
        mkMatch("qf", 3, "Quart 3", gsrc(B, 2), gsrc(A, 3)),
        mkMatch("qf", 4, "Quart 4", gsrc(B, 1), gsrc(A, 4)),
      ];
    } else if (groups.length === 4 && qualifiedPerGroup === 2) {
      const [A, B, C, D] = groups;
      first = [
        mkMatch("qf", 1, "Quart 1", gsrc(A, 1), gsrc(B, 2)),
        mkMatch("qf", 2, "Quart 2", gsrc(C, 1), gsrc(D, 2)),
        mkMatch("qf", 3, "Quart 3", gsrc(B, 1), gsrc(A, 2)),
        mkMatch("qf", 4, "Quart 4", gsrc(D, 1), gsrc(C, 2)),
      ];
    } else {
      first = pairFirstRound("qf", "Quart", seedSources(8));
    }
  } else if (format === "r16") {
    first = pairFirstRound("r16", "Huitième", seedSources(16));
  }

  const matches = [...first];
  let prev = first;
  const nextRoundName = { r16: "qf", qf: "sf" };
  while (prev.length > 2) {
    const round = nextRoundName[prev[0].round];
    const labelPrefix = round === "qf" ? "Quart" : "Demi-finale";
    const next = [];
    for (let i = 0; i < prev.length / 2; i += 1) {
      next.push(
        mkMatch(round, i + 1, `${labelPrefix} ${i + 1}`, W(prev[2 * i]), W(prev[2 * i + 1])),
      );
    }
    matches.push(...next);
    prev = next;
  }
  // prev = 2 demi-finales (ou directement les 2 SF du format "sf")
  const final = mkMatch("final", 1, "Finale", W(prev[0]), W(prev[1]));
  matches.push(final);
  if (thirdPlace) {
    matches.push(mkMatch("third", 1, "Petite finale", L(prev[0]), L(prev[1])));
  }
  if (classification && (format === "qf" || format === "r16")) {
    const qfs = matches.filter((m) => m.round === "qf");
    if (qfs.length === 4) {
      const c1 = mkMatch("c_sf", 1, "Classement 1", L(qfs[0]), L(qfs[1]));
      const c2 = mkMatch("c_sf", 2, "Classement 2", L(qfs[2]), L(qfs[3]));
      matches.push(
        c1,
        c2,
        mkMatch("c5", 1, "Match 5e place", W(c1), W(c2)),
        mkMatch("c7", 1, "Match 7e place", L(c1), L(c2)),
      );
    }
  }
  return matches;
}

// Libellé d'une source non résolue
export function sourceLabel(src, matchesById) {
  if (!src) return "À déterminer";
  if (src.type === "team") return "À déterminer";
  if (src.type === "group") return `${src.rank === 1 ? "1er" : `${src.rank}e`} ${src.group}`;
  if (src.type === "seed") return `Qualifié n°${src.rank}`;
  const ref = matchesById?.[src.match_id];
  const name = ref?.label || "match précédent";
  return src.type === "winner" ? `Vainqueur ${name}` : `Perdant ${name}`;
}

// Résolution dynamique du tableau complet
export function resolveBracket(teams, knockout) {
  const koMatches = knockout?.matches || [];
  const sorter = leagueSorter(knockout?.tiebreaker);
  const groups = {};
  teams.forEach((t) => {
    const g = (t.group || "").trim();
    if (g) (groups[g] ||= []).push(t);
  });
  Object.values(groups).forEach((arr) => arr.sort(sorter));

  const qpg = knockout?.qualified_per_group || 2;
  let seedTable;
  const gNames = Object.keys(groups);
  if (gNames.length) {
    const quals = [];
    gNames.forEach((g) =>
      groups[g].slice(0, qpg).forEach((t, idx) => quals.push({ team: t, rank: idx + 1 })),
    );
    quals.sort((a, b) => a.rank - b.rank || sorter(a.team, b.team));
    seedTable = quals.map((q) => q.team);
  } else {
    seedTable = [...teams].sort(sorter);
  }

  const byId = Object.fromEntries(koMatches.map((m) => [m.id, m]));
  const memo = {};
  const teamById = Object.fromEntries(teams.map((t) => [t.id, t]));

  const resolveMatch = (m) => {
    if (memo[m.id]) return memo[m.id];
    const home = m.home_override
      ? teamById[m.home_override] || null
      : resolveSource(m.home_source);
    const away = m.away_override
      ? teamById[m.away_override] || null
      : resolveSource(m.away_source);
    let winner = null;
    let loser = null;
    if (home && away && isKOPlayed(m)) {
      const hs = Number(m.home_score);
      const as = Number(m.away_score);
      const homeWins = hs !== as ? hs > as : Number(m.home_pens) > Number(m.away_pens);
      winner = homeWins ? home : away;
      loser = homeWins ? away : home;
    }
    memo[m.id] = { home, away, winner, loser };
    return memo[m.id];
  };

  const resolveSource = (src) => {
    if (!src) return null;
    if (src.type === "group") return groups[src.group]?.[src.rank - 1] || null;
    if (src.type === "seed") return seedTable[src.rank - 1] || null;
    if (src.type === "team") return teamById[src.team_id] || null;
    const ref = byId[src.match_id];
    if (!ref) return null;
    const r = resolveMatch(ref);
    return src.type === "winner" ? r.winner : r.loser;
  };

  return koMatches.map((m) => {
    const r = resolveMatch(m);
    return {
      ...m,
      home_team: r.home,
      away_team: r.away,
      winner: r.winner,
      loser: r.loser,
      played: isKOPlayed(m),
    };
  });
}

// Classement final 1re → 8e place à partir des matchs résolus
export function finalRanking(resolved) {
  const byRound = {};
  resolved.forEach((m) => {
    (byRound[m.round] ||= []).push(m);
  });
  const out = [];
  const push = (pos, team) => team && out.push({ position: pos, team });
  const final = byRound.final?.[0];
  if (!final?.played) return [];
  push(1, final.winner);
  push(2, final.loser);
  const third = byRound.third?.[0];
  if (third?.played) {
    push(3, third.winner);
    push(4, third.loser);
  }
  const c5 = byRound.c5?.[0];
  if (c5?.played) {
    push(5, c5.winner);
    push(6, c5.loser);
  }
  const c7 = byRound.c7?.[0];
  if (c7?.played) {
    push(7, c7.winner);
    push(8, c7.loser);
  }
  return out;
}

// Classement final corrigé manuellement (knockout.final_ranking_override)
export function overrideRanking(teams, knockout) {
  const byId = Object.fromEntries(teams.map((t) => [t.id, t]));
  return (knockout?.final_ranking_override || [])
    .map((r) => ({ position: r.position, team: byId[r.team_id] }))
    .filter((r) => r.team)
    .sort((a, b) => a.position - b.position);
}

// Matchs suivants déjà scorés qui dépendent (directement ou non) d'un match donné
export function scoredDownstream(koMatches, matchId) {
  const dependents = (id) =>
    koMatches.filter(
      (m) =>
        (m.home_source?.match_id === id && ["winner", "loser"].includes(m.home_source?.type)) ||
        (m.away_source?.match_id === id && ["winner", "loser"].includes(m.away_source?.type)),
    );
  const seen = new Set();
  const stack = [matchId];
  const impacted = [];
  while (stack.length) {
    const cur = stack.pop();
    dependents(cur).forEach((m) => {
      if (seen.has(m.id)) return;
      seen.add(m.id);
      if (hasScore(m.home_score) || hasScore(m.away_score)) impacted.push(m);
      stack.push(m.id);
    });
  }
  return impacted;
}
